import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import { KineticLines, SectionHead } from "@/components/Motion";
import { PostCard, ProductCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import { SITE } from "@/config/site";
import api from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data))
      .catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  if (!loading && !post) return <NotFound />;
  if (!post) return <div className="min-h-[60vh]" />;

  const crumbs = [{ label: "Insights", to: "/insights" }, { label: post.title }];
  const jsonLd = [
    breadcrumbSchema(crumbs),
    {
      "@context": "https://schema.org", "@type": "Article",
      headline: post.title, description: post.excerpt, image: post.image,
      author: { "@type": "Organization", name: post.author || SITE.brand },
      publisher: { "@type": "Organization", name: SITE.legalName },
      datePublished: post.date,
    },
  ];

  return (
    <div data-testid="blog-post-page">
      <SEO title={post.title} path={`/insights/${slug}`} description={post.excerpt}
        image={post.image} type="article" jsonLd={jsonLd} />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={crumbs} />
          <p className="eyebrow eyebrow-light">{post.category}</p>
          <KineticLines lines={[post.title]}
            className="mt-5 font-display text-3xl font-medium leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-5xl" />
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
            {post.author} · {post.date}
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="spotlight-img overflow-hidden border border-line">
          <img src={post.image} alt={post.title} className="aspect-[16/8] w-full object-cover" />
        </div>
        <p className="mt-10 text-xl font-light leading-relaxed text-ink">{post.excerpt}</p>
        {post.content?.map((para, i) => (
          <p key={i} className="mt-6 text-base leading-[1.85] text-steel" data-testid={`article-para-${i}`}>{para}</p>
        ))}
        <div className="mt-12 flex flex-wrap gap-2">
          {post.tags?.map((t) => (
            <span key={t} className="border border-line bg-mist px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-steel">{t}</span>
          ))}
        </div>
        <div className="mt-12 border border-line bg-mist p-7">
          <p className="font-display text-lg font-semibold text-ink">Have a requirement this article touches on?</p>
          <p className="mt-2 text-sm text-steel">Our engineering team can help you specify the right equipment for your application.</p>
          <Link to="/request-a-quote" data-testid="article-rfq-button"
            className="group mt-4 inline-flex items-center gap-2 bg-accent px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-accent-dark">
            Request a Quote <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </article>

      {post.products?.length > 0 && (
        <section className="border-t border-line bg-white">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <SectionHead eyebrow="Related Equipment" title="Products Covered in This Article" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="article-products-grid">
              {post.products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {post.related_posts?.length > 0 && (
        <section className="border-t border-line bg-mist">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <SectionHead eyebrow="Continue Reading" title="Related Articles" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {post.related_posts.map((p, i) => <PostCard key={p.slug} post={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
      <CTASection />
    </div>
  );
}
